import { type IncomeSplitOverviewData } from '@fruitful/income-split';
import {
    extractToolResultsFromThreadMessage,
    isRecord,
    isToolNameMatch,
    type MastraThreadMessage,
    unwrapMcpToolPayload,
} from '@fruitful/llm-service-shared';

const INCOME_SPLIT_OVERVIEW_TOOL = 'overviewIncomeSplit_get';
const BANK_ACCOUNT_DETAILS_TOOL = 'allBankingAccountDetails_get';

export interface TranscriptCardHolder {
    id: string | null;
    syncteraPersonId: string | null;
    firstName: string | null;
}

export interface TranscriptCard {
    id: string;
    syncteraPersonId: string | null;
    form: string | null;
    status: string | null;
    lastFour: string | null;
}

export interface TranscriptBankAccount {
    bankAccountId: string | null;
    nickname: string | null;
    primaryUser: TranscriptCardHolder | null;
    jointUser: TranscriptCardHolder | null;
    cards: TranscriptCard[];
}

/**
 * Read-tool results a widget in this message could have been built from: the
 * latest result of each tool at or before the message, in thread order.
 */
export interface TranscriptWidgetSources {
    incomeSplitOverview?: IncomeSplitOverviewData;
    bankAccounts?: TranscriptBankAccount[];
}

const readString = (value: unknown): string | null =>
    typeof value === 'string' && value !== '' ? value : null;

/** The MCP server renames `amount` to `amountCents`; the overview components read `amount`. */
const restoreAmount = (value: unknown): unknown => {
    if (!isRecord(value) || value.amount !== undefined) {
        return value;
    }
    const { amountCents, ...rest } = value;
    return typeof amountCents === 'number'
        ? { ...rest, amount: amountCents }
        : value;
};

const restoreExecutionAmounts = (execution: unknown): unknown => {
    const restored = restoreAmount(execution);
    if (!isRecord(restored) || !Array.isArray(restored.transfers)) {
        return restored;
    }
    return { ...restored, transfers: restored.transfers.map(restoreAmount) };
};

const toIncomeSplitOverview = (
    payload: unknown,
): IncomeSplitOverviewData | null => {
    if (!isRecord(payload) || !Array.isArray(payload.history)) {
        return null;
    }
    const history = payload.history.filter(isRecord).map((month) => ({
        ...month,
        executions: Array.isArray(month.executions)
            ? month.executions.map(restoreExecutionAmounts)
            : [],
    }));
    return {
        ...payload,
        configs: Array.isArray(payload.configs) ? payload.configs : [],
        history,
    } as unknown as IncomeSplitOverviewData;
};

const toCardHolder = (value: unknown): TranscriptCardHolder | null =>
    isRecord(value)
        ? {
              id: readString(value.id),
              syncteraPersonId: readString(value.syncteraPersonId),
              firstName: readString(value.firstName),
          }
        : null;

const toCard = (value: unknown): TranscriptCard | null => {
    if (!isRecord(value)) {
        return null;
    }
    const id = readString(value.id);
    return id == null
        ? null
        : {
              id,
              syncteraPersonId: readString(value.syncteraPersonId),
              form: readString(value.form),
              status: readString(value.cardStatus) ?? readString(value.status),
              lastFour: readString(value.lastFour),
          };
};

const toBankAccounts = (payload: unknown): TranscriptBankAccount[] | null => {
    if (!isRecord(payload) || !Array.isArray(payload.bankAccountDetails)) {
        return null;
    }
    return payload.bankAccountDetails.filter(isRecord).map((account) => ({
        bankAccountId: readString(account.bankAccountId),
        nickname: readString(account.nickname) ?? readString(account.name),
        primaryUser: toCardHolder(account.primaryUser),
        jointUser: toCardHolder(account.jointUser),
        cards: Array.isArray(account.cards)
            ? account.cards
                  .map(toCard)
                  .filter((card): card is TranscriptCard => card != null)
            : [],
    }));
};

const toTime = (value: MastraThreadMessage['createdAt']): number =>
    value == null ? 0 : new Date(value).getTime();

/** Widget source data keyed by message id. */
export const buildTranscriptWidgetSources = (
    messages: MastraThreadMessage[],
): Map<string, TranscriptWidgetSources> => {
    const sourcesByMessageId = new Map<string, TranscriptWidgetSources>();
    let latest: TranscriptWidgetSources = {};

    const ordered = [...messages].sort(
        (a, b) => toTime(a.createdAt) - toTime(b.createdAt),
    );
    for (const message of ordered) {
        for (const { toolName, result } of extractToolResultsFromThreadMessage(
            message,
        )) {
            if (isToolNameMatch(toolName, INCOME_SPLIT_OVERVIEW_TOOL)) {
                const overview = toIncomeSplitOverview(
                    unwrapMcpToolPayload(result),
                );
                if (overview != null) {
                    latest = { ...latest, incomeSplitOverview: overview };
                }
            } else if (isToolNameMatch(toolName, BANK_ACCOUNT_DETAILS_TOOL)) {
                const bankAccounts = toBankAccounts(
                    unwrapMcpToolPayload(result),
                );
                if (bankAccounts != null) {
                    latest = { ...latest, bankAccounts };
                }
            }
        }
        sourcesByMessageId.set(message.id, latest);
    }

    return sourcesByMessageId;
};
